import { expressURL, getAuthHeaders } from '../utils/networkUtils';
import { readFetchError } from '../utils/fetchError.ts';

export type SchoolNotice = {
  id: string;
  kind: string;
  category?: string | null;
  title: string;
  body: string;
  createdAt: string;
  laboratory?: boolean;
};

export type SchoolSafetyCase = {
  id: string;
  severity: string;
  status: string;
  category: string;
  createdAt: string;
  laboratory?: boolean;
  unblind?: {
    requested: boolean;
    approvalCount: number;
    expiresAt: string | null;
    expired: boolean;
  };
  excerpt?: {
    text: string;
    senderShortName: string;
    sentAt: string;
  };
};

export type ReviewExcerpt = {
  excerptToken: string;
  text: string;
  relativeTime?: string;
  laboratory?: boolean;
  classifier?: string;
};

type SchoolFetchOptions = Omit<RequestInit, 'body'> & { body?: unknown };

function apiUrl(path: string): string {
  return `${expressURL().replace(/\/$/, '')}${path}`;
}

async function schoolFetch<T>(path: string, options: SchoolFetchOptions = {}): Promise<T> {
  const auth = getAuthHeaders() as { headers?: Record<string, string> };
  const headers: Record<string, string> = { ...(auth.headers || {}) };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  const response = await fetch(apiUrl(path), {
    ...options,
    credentials: 'include',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body)
  });
  if (!response.ok) throw new Error(await readFetchError(response));
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export function fetchSchoolNotices() {
  return schoolFetch<{ notices: SchoolNotice[] }>('/api/school/notices');
}

export function fetchReviewQueue() {
  return schoolFetch<{ items: ReviewExcerpt[] }>('/api/school/review/queue');
}

export function decideReviewExcerpt(excerptToken: string, decision: string) {
  return schoolFetch<{ ok: boolean }>(`/api/school/review/${encodeURIComponent(excerptToken)}/decide`, {
    method: 'POST',
    body: { decision }
  });
}

export function fetchSchoolCases() {
  return schoolFetch<{ cases: SchoolSafetyCase[] }>('/api/school/cases');
}

export function fetchSchoolCase(id: string) {
  return schoolFetch<{ case: SchoolSafetyCase }>(`/api/school/cases/${encodeURIComponent(id)}`);
}

export function requestCaseUnblind(id: string) {
  return schoolFetch<{ case: SchoolSafetyCase }>(
    `/api/school/cases/${encodeURIComponent(id)}/unblind/request`,
    { method: 'POST', body: {} }
  );
}

export function approveCaseUnblind(id: string) {
  return schoolFetch<{ case: SchoolSafetyCase }>(
    `/api/school/cases/${encodeURIComponent(id)}/unblind/approve`,
    { method: 'POST', body: {} }
  );
}

export function requestSchoolHelp(audience: 'class-teacher' | 'safeguarding-lead' | 'parent', threadId?: string | number) {
  return schoolFetch<{ ok: boolean; routedTo: string }>('/api/school/help', {
    method: 'POST',
    body: { audience, threadId }
  });
}

export type SchoolSafetyPublicKey = {
  schoolId: string;
  publicKeyJwk: JsonWebKey;
  laboratory?: boolean;
  notice?: string;
};

let cachedSafetyKey: SchoolSafetyPublicKey | null = null;

export function fetchSchoolSafetyKey() {
  if (cachedSafetyKey) return Promise.resolve(cachedSafetyKey);
  return schoolFetch<SchoolSafetyPublicKey>('/api/school/safety-key').then((key) => {
    cachedSafetyKey = key;
    return key;
  });
}
